import { H1, Skeleton } from '@dnb/eufemia';
import Markdown from '@portals/shared-frontend/components/Markdown';

import { HeroContent } from '@/components/HeroPage/HeroContent';
import HeroMenu from '@/components/HeroPage/HeroMenu';
import { NavigationMenu } from '@/components/NavigationMenu';
import { useBackend } from '@/hooks/useBackend';
import useSWRExpect404 from '@/hooks/useSWRExpect404';
import { EventKafkaConfigResponse } from '@/openapi/schema';
import { toJsonMarkdown } from '@/utils/markdownUtils';

import EventResourceNotFoundError from '../EventResourceNotFoundError';

import style from './KafkaConfigView.module.css';

interface Props {
  environmentSlug: string;
  eventSlug: string;
}

const navigationItems = [
  { displayName: 'General Configuration', slug: 'general-configuration' },
  { displayName: 'Topic Configuration', slug: 'topic-configuration' },
];

export default function KafkaConfigView({ environmentSlug, eventSlug }: Props) {
  const swrExpect404 = useSWRExpect404();
  const { data: kafkaConfig, isLoading: isLoadingKafkaConfig } = useBackend(
    `/events/{eventSlug}/environments/{environmentSlug}/kafka-config`,
    {
      params: {
        path: {
          eventSlug: eventSlug,
          environmentSlug: environmentSlug,
        },
      },
    },
    swrExpect404,
  );

  return (
    <>
      {kafkaConfig && (
        <HeroMenu noPadding>
          <NavigationMenu items={navigationItems} />
        </HeroMenu>
      )}
      <HeroContent>
        <Skeleton show={isLoadingKafkaConfig}>
          {kafkaConfig ? (
            <div className={style['Content']}>
              <div>
                <H1 bottom="small" id="general-configuration" size="x-large" top="0">
                  General Configuration
                </H1>
                <Markdown>{getTopicConfig(kafkaConfig)}</Markdown>
              </div>
              <div>
                <H1 bottom="small" id="topic-configuration" size="x-large" top="0">
                  Topic Configuration
                </H1>
                <Markdown>{toJsonMarkdown(JSON.stringify(kafkaConfig.topicConfiguration, null, '\t'))}</Markdown>
              </div>
            </div>
          ) : (
            <EventResourceNotFoundError type="kafka-config" />
          )}
        </Skeleton>
      </HeroContent>
    </>
  );
}

function getTopicConfig(schema: EventKafkaConfigResponse): string {
  const deletionPreventionExists = schema.topicConfiguration['cleanup.policy'].includes('delete');
  return `| Partitions | Replication factor | Kafka platform | Cleanup policy | ${deletionPreventionExists ? 'Delete Retention ms | ' : ''}
  | ----------- | ----------- | ----------- | ----------- | ${deletionPreventionExists ? '----------- |' : ''}
  | ${schema.partitions} | ${schema.replicationFactor}| ${schema.kafkaPlatform} | ${schema.topicConfiguration['cleanup.policy'].join(', ')} | ${deletionPreventionExists && schema.topicConfiguration['delete.retention.ms']}`;
}
